"use client"

import { useState } from "react"
import { BulkEmail } from "@/schema"
import truncateText from "@/utils/truncate-text"
import { Badge, Button } from "@ui/shad"
import { format, formatDistanceToNow } from "date-fns"
import { CheckCircle2, Clock, Loader2, X, XCircle } from "lucide-react"

import { useCancelBulkEmail } from "@/hooks/api/bulk_emails"
import { ConfirmDialog } from "@/components/dialogs"

interface BulkEmailStatusItemProps {
  bulkEmail: BulkEmail
  description: BulkEmailStatusDescription
  onClick?: () => void
}

export interface BulkEmailStatusDescription {
  text: string
  showSentCount: boolean
}

const STATUS_CONFIG: Record<
  BulkEmail["status"],
  { icon: typeof Clock; label: string; className: string; iconClassName?: string }
> = {
  scheduled: {
    icon: Clock,
    label: "Scheduled",
    className: "bg-yellow-background text-yellow-foreground border-yellow-border",
  },
  sending: {
    icon: Clock,
    label: "Sending",
    className: "bg-blue-background text-blue-foreground border-blue-border",
  },
  completed: {
    icon: CheckCircle2,
    label: "Completed",
    className: "bg-green-background text-green-foreground border-green-border",
  },
  cancelled: {
    icon: XCircle,
    label: "Cancelled",
    className: "bg-secondary text-secondary-foreground border-border-accent",
  },
  failed: {
    icon: XCircle,
    label: "Failed",
    className: "bg-red-background text-red-foreground border-red-border",
  },
}

export function BulkEmailStatusItem({ bulkEmail, description, onClick }: BulkEmailStatusItemProps) {
  const config = STATUS_CONFIG[bulkEmail.status]
  const { mutate: cancelBulkEmail, isPending: isCancelling } = useCancelBulkEmail()
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const canCancel = bulkEmail.status === "scheduled" || bulkEmail.status === "sending"

  const handleCancelClick = () => {
    setShowCancelConfirm(true)
  }

  const confirmCancel = () => {
    setShowCancelConfirm(false)
    cancelBulkEmail(bulkEmail.id)
  }

  return (
    <>
      <div className="hover:bg-accent flex w-full flex-none items-center gap-3 p-3 transition-colors">
        <button type="button" onClick={onClick} className="min-w-0 flex-1 text-left">
          <p className="text-foreground truncate text-sm font-medium">
            {truncateText(bulkEmail.subject || "(No subject)", 50)}
          </p>
          <div className="text-muted-foreground mt-0.5 flex items-center gap-2 text-xs">
            <Badge size="sm" className={config.className}>
              {config.label}
            </Badge>
            {description.showSentCount && (
              <span>
                {bulkEmail.sent_count}/{bulkEmail.recipient_count} sent
              </span>
            )}
            <span>·</span>
            <span>{description.text}</span>
          </div>
        </button>
        {canCancel && (
          <Button variant="ghost" size="iconXs" onClick={handleCancelClick} disabled={isCancelling}>
            {isCancelling ? <Loader2 className="animate-spin" /> : <X />}
            <span className="sr-only">Cancel</span>
          </Button>
        )}
      </div>

      <ConfirmDialog
        open={showCancelConfirm}
        onOpenChange={setShowCancelConfirm}
        title={"Cancel bulk email?"}
        description={`Are you sure you want to cancel "${bulkEmail.subject || "(No subject)"}"? Any emails that haven't been sent yet will not be delivered.`}
        dismiss={"explicit"}
        cancel={{ onClick: () => setShowCancelConfirm(false) }}
        action={{ label: "Cancel email", onClick: confirmCancel }}
      />
    </>
  )
}

export function buildBulkEmailStatusDescription(bulkEmail: BulkEmail): BulkEmailStatusDescription {
  const timeAgo = formatDistanceToNow(new Date(bulkEmail.created_at), { addSuffix: true })
  const formattedStartAt = bulkEmail.scheduled_for ? format(new Date(bulkEmail.scheduled_for), "PP p") : null
  const estimatedFinishAt =
    bulkEmail.status !== "completed" && bulkEmail.status !== "cancelled" && bulkEmail.status !== "failed"
      ? bulkEmail.estimated_finish_at
      : null
  const formattedFinishAt = estimatedFinishAt ? format(new Date(estimatedFinishAt), "PP p") : null

  if (bulkEmail.status === "failed") {
    return { text: "Could not be sent", showSentCount: true }
  }

  if (formattedStartAt) {
    return { text: `Will start sending ${formattedStartAt}`, showSentCount: false }
  }

  if (formattedFinishAt) {
    return { text: `Will finish ${formattedFinishAt}`, showSentCount: true }
  }

  return { text: timeAgo, showSentCount: true }
}
